import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Church, LogIn, Info } from 'lucide-react';
import { login } from '../lib/auth';

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    const res = login(username.trim(), password);
    if (!res.ok) {
      setErr(res.error);
      setLoading(false);
      return;
    }
    navigate('/app/dashboard');
  };

  return (
    <div className="min-h-screen bg-[var(--gcbc-bg)] grid lg:grid-cols-2" data-testid="login-page">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex items-center justify-center p-12 text-white overflow-hidden bg-[var(--gcbc-royal-deep)]">
        <div className="absolute inset-0" aria-hidden style={{
          background: 'radial-gradient(600px 300px at 20% 10%, rgba(255,255,255,0.10), transparent 60%), radial-gradient(500px 300px at 80% 90%, rgba(225,29,72,0.30), transparent 60%)'
        }} />
        <div className="relative max-w-md">
          <Link to="/" className="inline-flex items-center gap-3 mb-8" data-testid="login-brand-link">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
              <Church className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold">Grants Creek Baptist Church</div>
              <div className="text-xs uppercase tracking-widest text-blue-100">Finances · 2026</div>
            </div>
          </Link>
          <h2 className="font-display text-4xl font-black leading-tight">
            Faithful stewardship,<br />clear numbers.
          </h2>
          <p className="mt-4 text-blue-100">
            Sign in to view February&apos;s ledger, adjust category budgets and print the balance sheet
            for the next business meeting.
          </p>
          <div className="mt-8 gcbc-accent-line rounded-full max-w-[120px]"></div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[var(--gcbc-royal)] text-white flex items-center justify-center">
              <Church className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">Grants Creek Baptist Church</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Finances · 2026</div>
            </div>
          </div>

          <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[var(--gcbc-red-deep)]">
              <LogIn className="w-3.5 h-3.5" /> Admin sign-in
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900 mt-2">Welcome back</h1>
            <p className="text-sm text-slate-600 mt-1">Only church admins can access the ledger.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Username</label>
                <input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[var(--gcbc-royal)] focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
                  placeholder="admin"
                  autoComplete="username"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Password</label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[var(--gcbc-royal)] focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
              {err && (
                <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}
              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-full bg-[var(--gcbc-royal)] hover:bg-[var(--gcbc-royal-deep)] text-white font-semibold transition-colors disabled:opacity-60"
              >
                {loading ? 'Signing in…' : 'Sign in to the ledger'}
              </button>
            </form>

            <div className="mt-5 flex items-start gap-2 text-xs text-slate-600 bg-blue-50/70 border border-blue-100 rounded-lg px-3 py-2.5">
              <Info className="w-4 h-4 text-[var(--gcbc-royal)] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-800">Demo credentials</div>
                Username <span className="font-mono">admin</span> · Password <span className="font-mono">gcbc2026</span>
              </div>
            </div>

            <Link to="/" className="block mt-4 text-center text-sm text-slate-500 hover:text-[var(--gcbc-royal)]" data-testid="login-back-link">
              ← Back to landing page
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
